import { ClipboardEvent, KeyboardEvent } from 'react'

import { hasBrandProfileValue } from 'utils/brandProfile'

export type SocialFieldKind = 'facebook' | 'instagram' | 'twitter'

export const PRICE_RANGE_ERROR_MESSAGE = "Minimum can't exceed the maximum"

/**
 * What a range's inputs are handed as their errorMessage: the design system
 * needs one to paint the field red, and the message itself is rendered once
 * under the pair instead of under each end.
 */
export const RANGE_ERROR_HIGHLIGHT = ' '

/**
 * A year or a price is never negative, and `type="number"` still lets a minus
 * sign be typed or pasted, so it is dropped as the value comes in.
 */
export const toNonNegativeInput = (value: string): string =>
  (value || '').replace(/-/g, '')

const DIGIT = /^\d$/

/**
 * Sanitising on change is not enough on its own: an entry `type="number"` reads
 * as invalid, such as "--34", reaches onChange as an empty string, so nothing is
 * left to clean while the browser keeps showing the text the user typed. These
 * fields hold whole, non-negative numbers, so a key that is not a digit is
 * refused before it ever lands in the field.
 */
export const blockNonDigitKey = (event: KeyboardEvent<HTMLElement>): void => {
  // Shortcuts (copy, paste, select all) and the named editing and navigation
  // keys - "Backspace", "Tab", "ArrowUp" - type nothing, so they are left alone.
  if (event.ctrlKey || event.metaKey || event.altKey) return
  if (event.key.length > 1) return
  if (!DIGIT.test(event.key)) event.preventDefault()
}

/**
 * Pasting never goes through a keystroke, so the clipboard is checked the same
 * way: a value that is not digits alone - "-50", "1e4", "$20" - is refused
 * rather than half-accepted.
 */
export const blockNonDigitPaste = (
  event: ClipboardEvent<HTMLElement>,
): void => {
  const pasted = event.clipboardData?.getData('text') ?? ''
  if (!/^\d+$/.test(pasted.trim())) event.preventDefault()
}

/**
 * The editor keeps what the user typed, so an end reaches here as a string, as
 * a number loaded from the profile, or empty. Anything that is not a usable
 * number - including the 0 that means "never filled in" - is no end at all.
 */
const toRangeEnd = (value?: string | number | null): number | null => {
  const raw = `${value ?? ''}`.trim()
  if (!raw) return null
  const parsed = Number(raw)
  return hasBrandProfileValue(parsed) ? parsed : null
}

/**
 * Only a range with both ends can be out of order: a half-filled one is valid
 * and renders as an open range on the profile.
 */
export const isPriceRangeInverted = (
  min?: string | number | null,
  max?: string | number | null,
): boolean => {
  const minEnd = toRangeEnd(min)
  const maxEnd = toRangeEnd(max)
  if (minEnd === null || maxEnd === null) return false
  return minEnd > maxEnd
}

// Splitting on facebook.com/ and taking the LAST segment handles values where
// a full url got pasted/duplicated inside another (e.g. a url-in-a-url), so
// we resolve to the innermost real page rather than the outer wrapper.
const FACEBOOK_DOMAIN_SPLIT = /facebook\.com\//i
// The split above matches anywhere, so require the value to actually START at
// the facebook domain before treating it as a url. Keeps the duplicated-url
// case working (the outer value still starts with the domain) while rejecting
// look-alikes such as "notfacebook.com/bar".
const FACEBOOK_DOMAIN_PREFIX = /^(https?:\/\/)?(www\.)?facebook\.com\//i

// A stored value that is just a page handle (no domain, no slashes) is a real
// value: it's what we append to www.facebook.com/. Anything else without a
// facebook.com/ segment is malformed.
export const FACEBOOK_BARE_TOKEN_PATTERN = /^[A-Za-z0-9._-]{1,50}$/
// Dots are legal in page handles ("joor.access"), so we only reject a bare
// value when it ends in a real TLD and is therefore a hostname - otherwise
// "facebook.com/" would resolve to the nonsense page www.facebook.com/facebook.com.
const LOOKS_LIKE_DOMAIN =
  /\.(com|net|org|co|io|me|uk|es|fr|de|it|us|ca|au|info|biz|tv|app)$/i
export const isBareFacebookToken = (value: string): boolean => {
  const trimmed = (value || '').trim()
  if (!FACEBOOK_BARE_TOKEN_PATTERN.test(trimmed)) return false
  return !LOOKS_LIKE_DOMAIN.test(trimmed)
}

// Extracts the real facebook page url from a value. Returns null when the value
// is neither a facebook.com/... url nor a bare page handle.
// Normalizes here (trim + strip surrounding slashes) so that every caller -
// validation and href building alike - resolves the exact same input and the
// two can never disagree.
export const extractFacebookPageUrl = (url: string): string | null => {
  const trimmed = (url || '').trim().replace(/^\/+|\/+$/g, '')
  if (!trimmed) return null
  const segments = trimmed.split(FACEBOOK_DOMAIN_SPLIT)
  if (segments.length < 2) {
    // no facebook.com/ segment: only a bare page handle is acceptable
    return isBareFacebookToken(trimmed)
      ? `https://www.facebook.com/${trimmed}`
      : null
  }
  // The split matches the bare substring, so "notfacebook.com/x" would look
  // like a facebook url - require the domain to start the value (optionally
  // after a protocol/www) before trusting it.
  if (!FACEBOOK_DOMAIN_PREFIX.test(trimmed)) return null
  const page = segments[segments.length - 1].trim().replace(/^\/+|\/+$/g, '')
  if (!page || /\s/.test(page)) return null
  return `https://www.facebook.com/${page}`
}

// Facebook: a facebook.com/something url, or a bare page handle. Empty is valid
// because social media is optional.
export const validateFacebookUrl = (url: string) => {
  const trimmed = (url || '').trim()
  if (!trimmed) return true
  return extractFacebookPageUrl(trimmed) !== null
}

// Instagram/Twitter: username only (no @) - 1–30 chars, letters, numbers, dots, underscores
const USERNAME_PATTERN = /^[A-Za-z0-9._]{1,30}$/
export const validateUsername = (username: string) => {
  const trimmed = (username || '').trim()
  return !trimmed || USERNAME_PATTERN.test(trimmed)
}

export const validateSocialField = (
  kind: SocialFieldKind,
  value: string,
): boolean => {
  if (kind === 'facebook') return validateFacebookUrl(value)
  return validateUsername(value)
}

// Normalize backend value to username only for display.
// Backend may send: full URL (instagram.com/foo), @username, or just the word (foo).
// All are valid; we show the username and save with the expected prefix.
const INSTAGRAM_URL_REGEX =
  /^(?:https?:\/\/)?(?:www\.)?instagram\.com\/@?([A-Za-z0-9._]+)\/?$/i
const TWITTER_URL_REGEX =
  /^(?:https?:\/\/)?(?:www\.)?(?:twitter|x)\.com\/@?([A-Za-z0-9_]+)\/?$/i
const FACEBOOK_URL_REGEX =
  /^(?:https?:\/\/)?(?:www\.)?facebook\.com\/([^/]+)\/?$/i

export const backendUrlToDisplayUsername = (
  kind: SocialFieldKind,
  url: string | null | undefined,
): string => {
  const value = (url || '').trim()
  if (!value) return ''
  if (kind === 'instagram') {
    const match = value.match(INSTAGRAM_URL_REGEX)
    if (match) return match[1]
    return value.replace(/^@+/, '')
  }
  if (kind === 'twitter') {
    const match = value.match(TWITTER_URL_REGEX)
    if (match) return match[1]
    return value.replace(/^@+/, '')
  }
  const match = value.match(FACEBOOK_URL_REGEX)
  if (match) return match[1]
  return value
}

// Format username for save: @username for Instagram/X; other kinds unchanged.
export const formatSocialUrlForSave = (
  kind: SocialFieldKind,
  displayValue: string | null | undefined,
): string => {
  const trimmed = (displayValue || '').trim()
  if (!trimmed) return ''
  if (kind === 'instagram' || kind === 'twitter') {
    return trimmed.startsWith('@') ? trimmed : `@${trimmed}`
  }
  return trimmed
}

export const getErrorMessage = (kind: SocialFieldKind): string => {
  if (kind === 'facebook') {
    return 'Please use a valid Facebook URL (www.facebook.com/something)'
  }
  return 'Please enter a valid username (letters, numbers, dots, underscores)'
}

/**
 * Returns whether a stored (kind, url) is valid for display as a social link.
 * Applied to the raw stored value (before any frontend display transform).
 * For Instagram/Twitter we accept any form that normalizes to a valid username:
 * - "hola", "@hola", "https://x.com/hola", "www.x.com/hola" → all valid (username "hola").
 * We only reject when the effective username is invalid (e.g. "hello how are you", "bad!user").
 */
export const isValidStoredSocialMediaForDisplay = (
  kind: SocialFieldKind,
  url?: string | null,
): boolean => {
  const value = (url || '').trim()
  if (!value) return false
  if (kind === 'facebook') return validateFacebookUrl(value)
  const regex = kind === 'instagram' ? INSTAGRAM_URL_REGEX : TWITTER_URL_REGEX
  const match = value.match(regex)
  const username = match
    ? match[1]
    : value
        .replace(/^@+/, '')
        .trim()
        .replace(/^\/+|\/+$/g, '')
  return validateUsername(username)
}

/**
 * Build the href for a social media link from kind and stored value.
 * For Instagram/Twitter, accepts any form (username, @username, full URL with
 * or without @) — extracts the plain username and rebuilds the href from it.
 * Returns null if the value is invalid or should not be shown.
 */
export const getSocialMediaHref = (
  kind: SocialFieldKind,
  url?: string | null,
): string | null => {
  if (!url?.trim()) return null
  if (!isValidStoredSocialMediaForDisplay(kind, url)) return null

  // One resolver for both halves: whatever validation accepted, this returns,
  // so the editor and the rendered link can never disagree.
  if (kind === 'facebook') {
    return extractFacebookPageUrl(url)
  }

  const username = backendUrlToDisplayUsername(kind, url)
  if (!username) return null
  if (kind === 'instagram') return `https://instagram.com/${username}`
  return `https://x.com/${username}`
}
